import React from 'react';
import styled from 'styled-components';
import { oneOf, shape, string } from 'prop-types';
import { scriptPropType } from '@bbc/gel-foundations/prop-types';
import { GEL_GROUP_3_SCREEN_WIDTH_MIN, MEDIA_QUERY_TYPOGRAPHY } from '@bbc/gel-foundations/breakpoints';
import { GEL_SPACING, GEL_SPACING_DBL } from '@bbc/gel-foundations/spacings';
import { getLongPrimer, getDoublePica } from '@bbc/gel-foundations/typography';
import { C_EBON, C_GHOST } from '@bbc/psammead-styles/colours';
import { getSansBold, getSansRegular } from '@bbc/psammead-styles/font-styles';
var minClickableHeightPx = 44;
var minClickableHeightRem = minClickableHeightPx / 16;

var paddingDir = function paddingDir(_ref) {
  var dir = _ref.dir;
  return "padding-".concat(dir === 'rtl' ? 'left' : 'right');
};

var paddingReverseDir = function paddingReverseDir(_ref2) {
  var dir = _ref2.dir;
  return "padding-".concat(dir === 'rtl' ? 'right' : 'left');
}; // Flex doesn't work right on IE11.
// This makes it work right. I don't fully understand how, but am
// eternally grateful to the Flexbugs project.
// https://github.com/philipwalton/flexbugs#flexbug-3


var FlexColumn = /*#__PURE__*/styled.span.withConfig({
  displayName: "FlexColumn",
  componentId: "wx5kdp-0"
})(["display:flex;flex-direction:column;"]);
var SectionLabelLink = /*#__PURE__*/styled.a.attrs(function (props) {
  return {
    'aria-labelledby': props.labelId
  };
}).withConfig({
  displayName: "SectionLabelLink",
  componentId: "wx5kdp-1"
})(["color:", ";text-decoration:none;&:focus,&:hover{text-decoration:underline;}"], C_EBON);
SectionLabelLink.propTypes = {
  href: string.isRequired,
  labelId: string.isRequired
};
var FlexRow = /*#__PURE__*/styled.span.withConfig({
  displayName: "FlexRow",
  componentId: "wx5kdp-2"
})(["display:flex;flex-flow:row nowrap;justify-content:space-between;min-height:", "rem;align-items:baseline;", "{align-items:stretch;}"], minClickableHeightRem, MEDIA_QUERY_TYPOGRAPHY.LAPTOP_AND_LARGER);
var FlexTextRow = /*#__PURE__*/styled(FlexRow).attrs({
  role: 'text'
}).withConfig({
  displayName: "FlexTextRow",
  componentId: "wx5kdp-3"
})([""]);
var titleMargins = "\n  margin: ".concat(GEL_SPACING_DBL, " 0;\n\n  ").concat(MEDIA_QUERY_TYPOGRAPHY.LAPTOP_AND_LARGER, " {\n    margin: 0;\n  }\n");
var Title = /*#__PURE__*/styled.span.withConfig({
  displayName: "Title",
  componentId: "wx5kdp-4"
})(["", ";", " background-color:", ";", ";", ":", ";@media (min-width:", "){", ":", ";}display:flex;align-items:center;"], function (_ref3) {
  var script = _ref3.script;
  return script && getDoublePica(script);
}, function (_ref4) {
  var service = _ref4.service;
  return getSansRegular(service);
}, function (props) {
  return props.backgroundColor;
}, titleMargins, paddingDir, GEL_SPACING, GEL_GROUP_3_SCREEN_WIDTH_MIN, paddingDir, GEL_SPACING_DBL);
Title.propTypes = {
  dir: oneOf(['ltr', 'rtl']).isRequired,
  id: string.isRequired,
  script: shape(scriptPropType).isRequired,
  service: string.isRequired
};
var IndexLinkCta = /*#__PURE__*/styled.span.attrs({
  'aria-hidden': 'true'
}).withConfig({
  displayName: "IndexLinkCta",
  componentId: "wx5kdp-5"
})(["", ";", ";", ";color:", ";background-color:", ";white-space:nowrap;", ":", ";display:flex;align-items:center;"], function (_ref5) {
  var script = _ref5.script;
  return script && getLongPrimer(script);
}, function (_ref6) {
  var service = _ref6.service;
  return getSansBold(service);
}, titleMargins, C_EBON, function (props) {
  return props.backgroundColor;
}, paddingReverseDir, GEL_SPACING_DBL);
IndexLinkCta.propTypes = {
  dir: oneOf(['ltr', 'rtl']).isRequired,
  script: shape(scriptPropType).isRequired,
  service: string.isRequired
};
export var PlainTitle = function PlainTitle(_ref7) {
  var title = _ref7.children,
      dir = _ref7.dir,
      labelId = _ref7.labelId,
      script = _ref7.script,
      service = _ref7.service,
      backgroundColor = _ref7.backgroundColor;
  return /*#__PURE__*/React.createElement(FlexColumn, null, /*#__PURE__*/React.createElement(FlexRow, null, /*#__PURE__*/React.createElement(Title, {
    script: script,
    dir: dir,
    id: labelId,
    service: service,
    backgroundColor: backgroundColor
  }, title)));
};
PlainTitle.propTypes = {
  children: string.isRequired,
  dir: oneOf(['ltr', 'rtl']).isRequired,
  labelId: string.isRequired,
  script: shape(scriptPropType).isRequired,
  service: string.isRequired,
  backgroundColor: string
};
PlainTitle.defaultProps = {
  backgroundColor: C_GHOST
};
export var LinkTitle = function LinkTitle(_ref8) {
  var title = _ref8.children,
      dir = _ref8.dir,
      href = _ref8.href,
      labelId = _ref8.labelId,
      linkText = _ref8.linkText,
      script = _ref8.script,
      service = _ref8.service,
      backgroundColor = _ref8.backgroundColor;
  return /*#__PURE__*/React.createElement(SectionLabelLink, {
    href: href,
    labelId: labelId
  }, /*#__PURE__*/React.createElement(FlexColumn, null, /*#__PURE__*/React.createElement(FlexTextRow, null, /*#__PURE__*/React.createElement(Title, {
    id: labelId,
    dir: dir,
    script: script,
    service: service,
    backgroundColor: backgroundColor
  }, title), /*#__PURE__*/React.createElement(IndexLinkCta, {
    dir: dir,
    script: script,
    service: service,
    backgroundColor: backgroundColor
  }, linkText))));
};
LinkTitle.propTypes = {
  children: string.isRequired,
  dir: oneOf(['ltr', 'rtl']).isRequired,
  href: string.isRequired,
  labelId: string.isRequired,
  linkText: string.isRequired,
  script: shape(scriptPropType).isRequired,
  service: string.isRequired,
  backgroundColor: string
};
LinkTitle.defaultProps = {
  backgroundColor: C_GHOST
};